import type { FieldFilter, FieldFilterOperator, Filter } from '#layers/data-model/types/filters';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import { isTemporal } from '#layers/base/public';
import { findCollectionPrimitiveFieldByApiName, findCollectionRelationFieldByApiName } from './fields/fields';
import { isFieldFilterOperator, isFilterRelationalQuantifier, isLogicalFilterAnd, isLogicalFilterOr, isUnaryFilterNot } from './filters';
import { resolveFieldParser } from './schema/primitive-field-parsers';
import { getTypename } from './schema/primitive-type';

/** Restores persisted filter operands with the parser declared by each schema field. */
export function hydrateFilterFromPersistence(filter: Filter, collection: ResolvedMonospaceCollection | undefined): Filter {
	if (isLogicalFilterAnd(filter)) {
		return { _and: filter._and.map((nestedFilter) => hydrateFilterFromPersistence(nestedFilter, collection)) };
	}

	if (isLogicalFilterOr(filter)) {
		return { _or: filter._or.map((nestedFilter) => hydrateFilterFromPersistence(nestedFilter, collection)) };
	}

	if (isUnaryFilterNot(filter)) {
		return { _not: hydrateFilterFromPersistence(filter._not, collection) };
	}

	return Object.fromEntries(
		Object.entries(filter).map(([fieldName, value]) => [fieldName, hydrateFieldValue(fieldName, value, collection)]),
	) as Filter;
}

/** Clones a filter while retaining immutable Temporal operands. */
export function cloneFilterForPersistence(filter: Filter): Filter {
	return cloneFilterValue(filter) as Filter;
}

function hydrateFieldValue(fieldName: string, value: unknown, collection: ResolvedMonospaceCollection | undefined): unknown {
	if (isFilterRelationalQuantifier(fieldName) && value && typeof value === 'object') {
		return hydrateFilterFromPersistence(value as Filter, collection);
	}

	const primitiveField = collection && findCollectionPrimitiveFieldByApiName(collection, fieldName);

	if (primitiveField) {
		const parser = resolveFieldParser(getTypename(primitiveField.type));
		return hydratePrimitiveFilterValue(value, parser?.parse);
	}

	const relationField = collection && findCollectionRelationFieldByApiName(collection, fieldName);

	if (!relationField?.oppositeCollection || !value || typeof value !== 'object') {
		return cloneFilterValue(value);
	}

	return hydrateFilterFromPersistence(value as Filter, relationField.oppositeCollection);
}

function hydratePrimitiveFilterValue(value: unknown, parse: ((value: unknown) => unknown) | undefined): unknown {
	if (!isOperatorExpression(value)) return hydrateOperand(value, parse);

	return Object.fromEntries(
		Object.entries(value as FieldFilterOperator).map(([operator, operand]) => [operator, hydrateOperand(operand, parse)]),
	);
}

function isOperatorExpression(value: unknown): value is FieldFilterOperator {
	return !!value && typeof value === 'object' && !Array.isArray(value) && isFieldFilterOperator(value as FieldFilter);
}

function hydrateOperand(value: unknown, parse: ((value: unknown) => unknown) | undefined): unknown {
	if (!parse || value == null) return cloneFilterValue(value);
	if (Array.isArray(value)) return value.map((item) => hydrateOperand(item, parse));

	const parsed = parse(value);
	return parsed ?? cloneFilterValue(value);
}

function cloneFilterValue(value: unknown): unknown {
	if (isTemporal(value)) return value;
	if (Array.isArray(value)) return value.map((item) => cloneFilterValue(item));

	if (value && typeof value === 'object') {
		return Object.fromEntries(Object.entries(value).map(([key, nestedValue]) => [key, cloneFilterValue(nestedValue)]));
	}

	return value;
}
